import {
  and,
  asc,
  attachment,
  desc,
  entry_version,
  eq,
  gte,
  isNotNull,
  journal_entry,
  journal_entry_tag,
  journal_tag,
  like,
  lt,
  lte,
  or,
  type PowerSyncDrizzleDB,
  type SQL,
  sql,
} from "@journal/powersync";
import { liveJournalEntryFilters } from "../live/filters";
import { monthDayOf } from "./dates";

const DEFAULT_SEARCH_LIMIT = 50;

/**
 * Trash (L2): normal views ONLY see live entries. The `deleted_at IS NULL`
 * invariant lives in the centralized live layer (SOFT_DELETE_STANDARD, Rule A);
 * here we compose it via `liveJournalEntryFilters()`. The trash
 * (`getDeletedEntriesQuery`) is the only exception and uses the inverse.
 */
const [notDeleted] = liveJournalEntryFilters();

/**
 * Entries for a specific day (Today screen / calendar day),
 * in write order.
 */
export function getEntriesByDateQuery(db: PowerSyncDrizzleDB, userId: string, dateKey: string) {
  return db
    .select()
    .from(journal_entry)
    .where(
      and(eq(journal_entry.user_id, userId), eq(journal_entry.entry_date, dateKey), notDeleted),
    )
    .orderBy(asc(journal_entry.created_at));
}

/** Entries in an inclusive [start, end] range (range export). */
export function getEntriesInRangeQuery(
  db: PowerSyncDrizzleDB,
  userId: string,
  startKey: string,
  endKey: string,
) {
  return db
    .select()
    .from(journal_entry)
    .where(
      and(
        eq(journal_entry.user_id, userId),
        sql`${journal_entry.entry_date} >= ${startKey}`,
        sql`${journal_entry.entry_date} <= ${endKey}`,
        notDeleted,
      ),
    )
    .orderBy(asc(journal_entry.entry_date), asc(journal_entry.created_at));
}

/** One entry by id (detail/edit). */
export function getEntryByIdQuery(db: PowerSyncDrizzleDB, entryId: string) {
  return db
    .select()
    .from(journal_entry)
    .where(and(eq(journal_entry.id, entryId), notDeleted));
}

/**
 * Days with entries, newest first (streaks + calendar points).
 * Returns rows { entry_date, entries } aggregated by day.
 */
export function getEntryDaysQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db
    .select({
      entry_date: journal_entry.entry_date,
      entries: sql<number>`count(${journal_entry.id})`.mapWith(Number),
    })
    .from(journal_entry)
    .where(and(eq(journal_entry.user_id, userId), notDeleted))
    .groupBy(journal_entry.entry_date)
    .orderBy(desc(journal_entry.entry_date));
}

/**
 * Instant local full-text search.
 *
 * PowerSync's SQLite (wa-sqlite) does not include compiled FTS5 → LIKE over
 * title+body with lower() (case-insensitive ASCII; accents are not
 * folded — a limitation documented in PRODUCT_PLAN D2). 100% local.
 */
export function searchEntriesQuery(
  db: PowerSyncDrizzleDB,
  userId: string,
  term: string,
  limit: number = DEFAULT_SEARCH_LIMIT,
) {
  const pattern = `%${term.toLowerCase()}%`;
  return db
    .select()
    .from(journal_entry)
    .where(
      and(
        eq(journal_entry.user_id, userId),
        notDeleted,
        or(
          like(sql`lower(${journal_entry.body})`, pattern),
          like(sql`lower(${journal_entry.title})`, pattern),
        ),
      ),
    )
    .orderBy(desc(journal_entry.entry_date), desc(journal_entry.created_at))
    .limit(limit);
}

/**
 * Combined entry filter (F2): text + mood + tag + date range.
 *
 * Everything is optional and combined with AND. Text reuses the nocase LIKE from
 * `searchEntriesQuery` (D2). Tag filtering JOINs the bridge table. 100% local,
 * instant. With no active filters, it returns the most recent entries
 * (limited) — useful as a catalog to refine.
 */
export interface EntryFilters {
  term?: string;
  mood?: number;
  tagId?: string;
  startKey?: string;
  endKey?: string;
}

/**
 * The WHERE conditions of {@link filterEntriesQuery}, without a database handle, so
 * the SQL they produce can run against a real SQLite engine in tests.
 */
export function entryFilterConditions(userId: string, filters: EntryFilters): SQL[] {
  const conditions: SQL[] = [eq(journal_entry.user_id, userId), notDeleted];

  const term = filters.term?.trim().toLowerCase();
  if (term) {
    const pattern = `%${term}%`;
    const text = or(
      like(sql`lower(${journal_entry.body})`, pattern),
      like(sql`lower(${journal_entry.title})`, pattern),
    );
    if (text) {
      conditions.push(text);
    }
  }

  if (typeof filters.mood === "number") {
    conditions.push(eq(journal_entry.mood, filters.mood));
  }

  if (filters.startKey) {
    conditions.push(gte(journal_entry.entry_date, filters.startKey));
  }

  if (filters.endKey) {
    conditions.push(lte(journal_entry.entry_date, filters.endKey));
  }

  if (filters.tagId) {
    conditions.push(
      sql`${journal_entry.id} in (select ${journal_entry_tag.entry_id} from ${journal_entry_tag} where ${journal_entry_tag.user_id} = ${userId} and ${journal_entry_tag.tag_id} = ${filters.tagId})`,
    );
  }

  return conditions;
}

export function filterEntriesQuery(
  db: PowerSyncDrizzleDB,
  userId: string,
  filters: EntryFilters,
  limit: number = DEFAULT_SEARCH_LIMIT,
) {
  return db
    .select()
    .from(journal_entry)
    .where(and(...entryFilterConditions(userId, filters)))
    .orderBy(desc(journal_entry.entry_date), desc(journal_entry.created_at))
    .limit(limit);
}

/**
 * "On this day": entries from the same day/month (MM-DD) in previous years.
 */
export function getOnThisDayQuery(db: PowerSyncDrizzleDB, userId: string, todayKey: string) {
  const plan = onThisDayPlan(userId, todayKey);
  return db
    .select()
    .from(journal_entry)
    .where(plan.where)
    .orderBy(...plan.orderBy);
}

/** Filter and order of {@link getOnThisDayQuery}, without a database handle. */
export function onThisDayPlan(userId: string, todayKey: string) {
  const monthDay = monthDayOf(todayKey);
  const MONTH_DAY_START_1 = 6; // substr SQLite es 1-based: "YYYY-MM-DD" → MM-DD arranca en 6
  const MONTH_DAY_LENGTH = 5;
  return {
    where: and(
      eq(journal_entry.user_id, userId),
      notDeleted,
      eq(
        sql`substr(${journal_entry.entry_date}, ${MONTH_DAY_START_1}, ${MONTH_DAY_LENGTH})`,
        monthDay,
      ),
      lt(journal_entry.entry_date, todayKey),
    ),
    orderBy: [desc(journal_entry.entry_date), asc(journal_entry.created_at)],
  };
}

/** Favorite entries, newest first. */
export function getFavoriteEntriesQuery(db: PowerSyncDrizzleDB, userId: string) {
  const plan = favoriteEntriesPlan(userId);
  return db
    .select()
    .from(journal_entry)
    .where(plan.where)
    .orderBy(...plan.orderBy);
}

/** Filter and order of {@link getFavoriteEntriesQuery}, without a database handle. */
export function favoriteEntriesPlan(userId: string) {
  return {
    where: and(eq(journal_entry.user_id, userId), eq(journal_entry.is_favorite, 1), notDeleted),
    orderBy: [desc(journal_entry.entry_date), desc(journal_entry.created_at)],
  };
}

/** User tags sorted by name. */
export function getTagsQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db
    .select()
    .from(journal_tag)
    .where(eq(journal_tag.user_id, userId))
    .orderBy(asc(journal_tag.name));
}

/** Tags assigned to an entry (JOIN with the bridge table). */
export function getEntryTagsQuery(db: PowerSyncDrizzleDB, entryId: string) {
  return db
    .select({
      id: journal_tag.id,
      name: journal_tag.name,
      color: journal_tag.color,
      link_id: journal_entry_tag.id,
    })
    .from(journal_entry_tag)
    .innerJoin(journal_tag, eq(journal_tag.id, journal_entry_tag.tag_id))
    .where(eq(journal_entry_tag.entry_id, entryId))
    .orderBy(asc(journal_tag.name));
}

/**
 * Map of entry→tags for ALL of the user's entries in one query (for lists).
 * Returns flat rows { entry_id, tag_id, name, color }.
 */
export function getAllEntryTagsQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db
    .select({
      entry_id: journal_entry_tag.entry_id,
      tag_id: journal_tag.id,
      name: journal_tag.name,
      color: journal_tag.color,
    })
    .from(journal_entry_tag)
    .innerJoin(journal_tag, eq(journal_tag.id, journal_entry_tag.tag_id))
    .where(eq(journal_entry_tag.user_id, userId))
    .orderBy(asc(journal_tag.name));
}

/** Entries with a specific tag. */
export function getEntriesByTagQuery(db: PowerSyncDrizzleDB, userId: string, tagId: string) {
  return db
    .select({
      id: journal_entry.id,
      user_id: journal_entry.user_id,
      entry_date: journal_entry.entry_date,
      title: journal_entry.title,
      body: journal_entry.body,
      mood: journal_entry.mood,
      mood_tags: journal_entry.mood_tags,
      is_favorite: journal_entry.is_favorite,
      template_key: journal_entry.template_key,
      word_count: journal_entry.word_count,
      created_at: journal_entry.created_at,
      updated_at: journal_entry.updated_at,
      deleted_at: journal_entry.deleted_at,
    })
    .from(journal_entry_tag)
    .innerJoin(journal_entry, eq(journal_entry.id, journal_entry_tag.entry_id))
    .where(
      and(eq(journal_entry_tag.user_id, userId), eq(journal_entry_tag.tag_id, tagId), notDeleted),
    )
    .orderBy(desc(journal_entry.entry_date), desc(journal_entry.created_at));
}

/** Entry photos (attachment.entry_id), in upload order. */
export function getPhotosByEntryQuery(db: PowerSyncDrizzleDB, entryId: string) {
  return db
    .select()
    .from(attachment)
    .where(eq(attachment.entry_id, entryId))
    .orderBy(asc(attachment.created_at));
}

/** Photos from all of the user's entries (for day lists). */
export function getUserPhotosQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db
    .select()
    .from(attachment)
    .where(and(eq(attachment.user_id, userId), sql`${attachment.entry_id} IS NOT NULL`))
    .orderBy(asc(attachment.created_at));
}

/** Global totals: entries, distinct days, words. */
export function getJournalTotalsQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db
    .select({
      entries: sql<number>`count(${journal_entry.id})`.mapWith(Number),
      days: sql<number>`count(DISTINCT ${journal_entry.entry_date})`.mapWith(Number),
      words: sql<number>`COALESCE(sum(${journal_entry.word_count}), 0)`.mapWith(Number),
    })
    .from(journal_entry)
    .where(and(eq(journal_entry.user_id, userId), notDeleted));
}

/**
 * Soft monthly statistics (YYYY-MM): days written, entries and words.
 */
export function getMonthlyStatsQuery(db: PowerSyncDrizzleDB, userId: string, limit: number) {
  const MONTH_LENGTH = 7; // "YYYY-MM"
  const month = sql<string>`substr(${journal_entry.entry_date}, 1, ${MONTH_LENGTH})`;
  return db
    .select({
      month: month.as("month"),
      days: sql<number>`count(DISTINCT ${journal_entry.entry_date})`.mapWith(Number),
      entries: sql<number>`count(${journal_entry.id})`.mapWith(Number),
      words: sql<number>`COALESCE(sum(${journal_entry.word_count}), 0)`.mapWith(Number),
    })
    .from(journal_entry)
    .where(and(eq(journal_entry.user_id, userId), notDeleted))
    .groupBy(month)
    .orderBy(desc(month))
    .limit(limit);
}

/**
 * Mood distribution (F3 analytics, pure statistics without AI): for each
 * value 1–5, how many entries have that mood. Only entries with an assigned mood.
 */
export function getMoodDistributionQuery(db: PowerSyncDrizzleDB, userId: string) {
  const plan = moodDistributionPlan(userId);
  return db
    .select(plan.columns)
    .from(journal_entry)
    .where(plan.where)
    .groupBy(plan.groupBy)
    .orderBy(plan.orderBy);
}

/** Select list, filter, grouping and order of {@link getMoodDistributionQuery}, without a database handle. */
export function moodDistributionPlan(userId: string) {
  return {
    columns: {
      mood: journal_entry.mood,
      count: sql<number>`count(${journal_entry.id})`.mapWith(Number),
    },
    where: moodEntriesWhere(userId),
    groupBy: journal_entry.mood,
    orderBy: asc(journal_entry.mood),
  };
}

/** Entries that carry a mood and are not in the trash: the base of every mood aggregate. */
function moodEntriesWhere(userId: string) {
  return and(eq(journal_entry.user_id, userId), isNotNull(journal_entry.mood), notDeleted);
}

/**
 * Mood evolution by period (ISO week or month), for the trend chart. `bucket` =
 * "YYYY-MM" (month) or "YYYY-Www" derived on the client; here we group by month
 * in SQL (O(1) with substr) and the client re-aggregates by week when needed.
 * Returns average and count by month, ascending.
 *
 * Note: grouping by ISO week in SQLite without FTS/extensions is fragile
 * (strftime '%W' differs from ISO), so the week is calculated on the client over
 * raw rows (`getMoodEntriesQuery`); this query covers the cheap monthly case.
 */
export function getMonthlyMoodQuery(db: PowerSyncDrizzleDB, userId: string, limit: number) {
  const plan = monthlyMoodPlan(userId);
  return db
    .select(plan.columns)
    .from(journal_entry)
    .where(plan.where)
    .groupBy(plan.groupBy)
    .orderBy(plan.orderBy)
    .limit(limit);
}

/** Select list, filter, grouping and order of {@link getMonthlyMoodQuery}, without a database handle. */
export function monthlyMoodPlan(userId: string) {
  const MONTH_LENGTH = 7; // "YYYY-MM"
  const month = sql<string>`substr(${journal_entry.entry_date}, 1, ${MONTH_LENGTH})`;
  return {
    columns: {
      bucket: month.as("bucket"),
      avg: sql<number>`avg(${journal_entry.mood})`.mapWith(Number),
      count: sql<number>`count(${journal_entry.mood})`.mapWith(Number),
    },
    where: moodEntriesWhere(userId),
    groupBy: month,
    orderBy: asc(month),
  };
}

/**
 * Raw rows (entry_date, mood) from entries with a mood, ascending: input for
 * client-side WEEK aggregation (`bucketMoodByWeek`) and the trend.
 */
export function getMoodEntriesQuery(db: PowerSyncDrizzleDB, userId: string) {
  const plan = moodEntriesPlan(userId);
  return db
    .select(plan.columns)
    .from(journal_entry)
    .where(plan.where)
    .orderBy(...plan.orderBy);
}

/** Select list, filter and order of {@link getMoodEntriesQuery}, without a database handle. */
export function moodEntriesPlan(userId: string) {
  return {
    columns: {
      entry_date: journal_entry.entry_date,
      mood: journal_entry.mood,
    },
    where: moodEntriesWhere(userId),
    orderBy: [asc(journal_entry.entry_date), asc(journal_entry.created_at)],
  };
}

// ─── Trash (L2) ──────────────────────────────────────────────────────────────

/** Entries in the trash, most recently deleted first. */
export function getDeletedEntriesQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db
    .select()
    .from(journal_entry)
    .where(and(eq(journal_entry.user_id, userId), isNotNull(journal_entry.deleted_at)))
    .orderBy(desc(journal_entry.deleted_at));
}

// ─── Version history (L4) ────────────────────────────────────────────────────

/**
 * Local versions of an entry, newest first. The PowerSync bucket
 * downloads the latest 10 per entry (offline-first); deep history is
 * requested from the paginated API. id is UUIDv7 → sorting by id = sorting by time.
 */
export function getEntryVersionsQuery(db: PowerSyncDrizzleDB, entryId: string) {
  return db
    .select()
    .from(entry_version)
    .where(eq(entry_version.entry_id, entryId))
    .orderBy(desc(entry_version.id));
}
